import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { FadeIn, MaskedLine, MonoLabel } from "../components/motion";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";

const TABS = [
    { key: "all", label: "All Products", testId: "tab-all" },
    { key: "geocloth", label: "Geocloth", testId: "tab-geocloth" },
    { key: "geogrid", label: "Geogrid", testId: "tab-geogrid" },
    { key: "composite", label: "Composite Geocloth + Geogrid", testId: "tab-composite" },
];

const CATEGORY_LABELS = { geocloth: "Geocloth", geogrid: "Geogrid", composite: "Composite" };

const ProductCard = ({ p, index }) => {
    const { addItem } = useCart();
    return (
        <FadeIn delay={(index % 4) * 0.06} className="group relative border-r border-b border-line flex flex-col bg-[#050505]">
            <Link to={`/products/${p.slug}`} data-testid={`product-card-${p.slug}`} className="block">
                <div className="relative bg-white aspect-[4/3] overflow-hidden">
                    <img
                        src={p.image}
                        alt={p.name}
                        loading="lazy"
                        className="w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700"
                    />
                    <span className="absolute top-3 left-3 font-mono text-[10px] tracking-[0.2em] uppercase bg-black text-white px-2 py-1">
                        {CATEGORY_LABELS[p.category] || p.category}
                    </span>
                </div>
            </Link>
            <div className="flex-1 flex flex-col p-6">
                <Link to={`/products/${p.slug}`}>
                    <h3 className="font-heading font-bold tracking-tight text-lg leading-snug group-hover:text-[#FF5C00] transition-colors duration-300">
                        {p.name}
                    </h3>
                </Link>
                <p className="mt-2 text-sm text-white/55 leading-relaxed line-clamp-2">{p.short_description}</p>
                <div className="mt-4 space-y-1 font-mono text-[10px] tracking-wider uppercase text-white/40">
                    <p><span className="text-[#FF5C00]">APP</span> — {p.applications?.[0] || "General"}</p>
                    <p><span className="text-[#FF5C00]">SIZE</span> — {p.size || "Standard size"}</p>
                </div>
                <div className="mt-auto pt-6 flex items-center justify-between gap-3">
                    <span className="font-mono text-xs text-white/70" data-testid={`product-price-${p.slug}`}>
                        {p.price ? `$${p.price}` : "Request Pricing"}
                    </span>
                    <div className="flex gap-px">
                        <Link
                            to={`/products/${p.slug}`}
                            data-testid={`view-product-${p.slug}`}
                            className="h-10 px-4 flex items-center border border-white/20 font-mono text-[10px] tracking-[0.15em] uppercase text-white hover:bg-white hover:text-black transition-colors duration-300"
                        >
                            View
                        </Link>
                        <button
                            data-testid={`add-to-enquiry-${p.slug}`}
                            onClick={() => { addItem(p); toast.success("Added to order enquiry"); }}
                            className="h-10 px-4 flex items-center gap-2 bg-[#FF5C00] font-mono text-[10px] tracking-[0.15em] uppercase text-white hover:bg-white hover:text-black transition-colors duration-300"
                        >
                            <Plus className="w-3 h-3" /> Enquiry
                        </button>
                    </div>
                </div>
            </div>
        </FadeIn>
    );
};

const Products = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const cat = searchParams.get("cat") || "all";

    useEffect(() => {
        setLoading(true);
        api.get("/products", { params: cat === "all" ? {} : { category: cat } })
            .then((r) => setProducts(r.data))
            .catch(() => toast.error("Could not load products"))
            .finally(() => setLoading(false));
    }, [cat]);

    return (
        <div data-testid="products-page">
            <section className="border-b border-line px-4 md:px-8 pt-40 pb-14 md:pt-52 md:pb-20" data-testid="products-hero">
                <FadeIn><MonoLabel>Catalogue / Order Enquiry</MonoLabel></FadeIn>
                <h1 className="mt-6 font-heading font-black uppercase tracking-tighter leading-[0.9] text-[12vw] md:text-[7.5vw]" data-testid="products-headline">
                    <MaskedLine delay={0.15}>Products built for</MaskedLine>
                    <MaskedLine delay={0.27}><span className="text-[#FF5C00]">demanding ground.</span></MaskedLine>
                </h1>
                <FadeIn delay={0.35} className="mt-8 max-w-xl">
                    <p className="text-white/70 leading-relaxed text-base md:text-lg">
                        Browse the range, add products to an enquiry, and our team will confirm pricing, availability and delivery.
                    </p>
                </FadeIn>
            </section>

            <div className="sticky top-16 md:top-20 z-30 bg-black/80 backdrop-blur-xl border-b border-line overflow-x-auto" data-testid="product-tabs">
                <div className="flex min-w-max">
                    {TABS.map((t) => (
                        <button
                            key={t.key}
                            data-testid={t.testId}
                            onClick={() => setSearchParams(t.key === "all" ? {} : { cat: t.key })}
                            className={`px-6 md:px-8 h-14 font-mono text-xs tracking-[0.2em] uppercase border-r border-line transition-colors duration-300 ${
                                cat === t.key ? "bg-[#FF5C00] text-white" : "text-white/60 hover:text-white"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                </div>
            </div>

            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4" data-testid="products-loading">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="border-r border-b border-line">
                            <div className="aspect-[4/3] bg-charcoal animate-pulse" />
                            <div className="p-6 space-y-3">
                                <div className="h-4 bg-charcoal animate-pulse w-3/4" />
                                <div className="h-3 bg-charcoal animate-pulse w-full" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : products.length === 0 ? (
                <div className="p-24 text-center font-mono text-sm text-white/40 uppercase tracking-[0.25em]" data-testid="products-empty">
                    No products in this category yet
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 border-t border-line" data-testid="products-grid">
                    {products.map((p, i) => (
                        <ProductCard key={p.id} p={p} index={i} />
                    ))}
                </div>
            )}

            <section className="border-t border-line px-8 md:px-16 py-20 flex flex-col md:flex-row md:items-center justify-between gap-8" data-testid="products-cta">
                <p className="font-heading font-extrabold uppercase tracking-tight text-2xl md:text-4xl max-w-2xl">
                    Can't find what your project needs?
                </p>
                <Link
                    to="/contact"
                    data-testid="products-contact-cta"
                    className="group inline-flex items-center justify-between gap-6 bg-[#FF5C00] text-white px-10 h-14 font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors duration-300 shrink-0"
                >
                    Ask Our Team
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
            </section>
        </div>
    );
};

export default Products;
